import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "../ui";
import NativeInput from "../NativeInput";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { api } from "../../services";
import { invalidateSetupGuideCache } from "../../hooks/useSetupGuideStatus";

const CreateStoreModal = ({
  show,
  setShow,
  onStoreCreated, // Callback to refresh data after store creation
}) => {
  const { t } = useTranslation("translation");
  const [storeName, setStoreName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isValidEmail = (val) => /\S+@\S+\.\S+/.test(val);

  const toggleShow = () => {
    setError({});
    setStoreName("");
    setEmail("");
    setShow(false);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setStoreName("");
      setEmail("");
      setError({});
      setHasSubmitted(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [show]);

  // Always clear inline errors when the modal opens
  useEffect(() => {
    if (show) {
      setError({});
      setHasSubmitted(false);
    }
  }, [show]);

  const validateForm = () => {
    const errors = {};

    if (!storeName.trim()) {
      errors.storeName = t("Store name is required");
    }

    if (!email.trim()) {
      errors.email = t("Email is required");
    } else if (!isValidEmail(email.trim())) {
      errors.email = t("Please enter a valid email address");
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validateForm()) {
      // Scroll to the first error
      const firstError = Object.keys(error)[0];
      const errorElement = document.querySelector(`[data-error-field="${firstError}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await api.post(
        "/stores",
        {
          name: storeName.trim(),
          email: email.trim().toLowerCase(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(t("Store created successfully."));
        invalidateSetupGuideCache();
        toggleShow();
        if (onStoreCreated) {
          onStoreCreated(response.data.store || response.data);
        }
      }
    } catch (err) {
      console.error("Error creating store:", err);

      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        `${t("Failed to create store. Please try again.")}`;

      toast.error(errorMessage, { autoClose: 5000 });

      // Handle field-specific errors if returned by API
      if (err.response?.data?.errors) {
        const apiErrors = {};
        err.response.data.errors.forEach(error => {
          if (error.field === 'name') {
            apiErrors.storeName = error.message;
          } else if (error.field === 'email') {
            apiErrors.email = error.message;
          }
        });
        setError(apiErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="create-store-modal" role="presentation">
      <button
        type="button"
        className="create-store-modal__backdrop"
        aria-label={t("cancel")}
        onClick={toggleShow}
      />
      <div
        className="create-store-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-store-title"
      >
        <div className="create-store-modal__header">
          <h2 id="create-store-title">{t("Create Store")}</h2>
          <button type="button" className="create-store-modal__close" onClick={toggleShow} aria-label={t("cancel")}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="create-store-modal__body">
            <NativeInput
              data-error-field="storeName"
              label={
                <span>
                  {t("storename")}{" "}
                  <span className="text-danger">*</span>
                </span>
              }
              value={storeName}
              onChange={(e) => {
                const value = e.target.value;
                setStoreName(value);
                if (hasSubmitted) {
                  setError((prev) => {
                    if (!value.trim()) {
                      return {
                        ...prev,
                        storeName: t("Store name is required"),
                      };
                    }
                    const { storeName: _removed, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              invalid={!!error.storeName}
              feedback={error.storeName || ""}
            />
            <NativeInput
              data-error-field="email"
              label={
                <span>
                  {t("emailaddress")}{" "}
                  <span className="text-danger">*</span>
                </span>
              }
              type="email"
              value={email}
              onChange={(e) => {
                const value = e.target.value;
                setEmail(value);
                if (hasSubmitted) {
                  setError((prev) => {
                    if (!value.trim()) {
                      return { ...prev, email: t("Email is required") };
                    }
                    if (!isValidEmail(value.trim())) {
                      return {
                        ...prev,
                        email: t("Please enter a valid email address"),
                      };
                    }
                    const { email: _removed, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              invalid={!!error.email}
              feedback={error.email || ""}
            />
          </div>
          <div className="create-store-modal__footer">
            <Button color="secondary" onClick={toggleShow} disabled={loading}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t("creating") : t("create")}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateStoreModal;
